import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { color, font, fontSize, fontWeight, radius, shadow, zIndex } from '@/design/tokens';
import { useT } from '@/i18n';
import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import { Mono } from '@/design/primitives';

/**
 * Global patient/command search (DS-013).
 *
 * Results are whatever the server returns for this user. The endpoint applies
 * the same tenant scope, branch grants and field permissions as the patient
 * list, so this is not a back door into records the user cannot open - and the
 * footer says so, because a receptionist who cannot find a patient needs to
 * know whether the record is missing or merely out of scope.
 */

interface SearchHit {
  id: string;
  kind: 'patient' | 'appointment' | 'invoice';
  title: string;
  subtitle: string;
  /** e.g. "MRN-100482". Rendered Latin-locked. */
  code?: string;
  /** Permission-safe alert summary, e.g. "حساسية". Never the substance name. */
  alert?: string;
  route: string;
}

const QUICK_ACTIONS: { labelAr: string; route: string; permission: string }[] = [
  { labelAr: 'حجز موعد جديد', route: '/appointments?new=1', permission: 'appointments.create' },
  { labelAr: 'تسجيل مريض جديد', route: '/patients?new=1', permission: 'patients.create' },
];

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useSession();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHits([]);
      setCursor(0);
      // Focus after paint so the browser does not scroll the page behind.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Debounced. Patient search is indexed but still hits the database, and a
  // keystroke-per-request pattern is what turns a 750ms target into 3 seconds
  // under a full waiting room (PRD 21.2).
  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setHits([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      api
        .get<{ items: SearchHit[] }>('/search', {
          query: { q: query.trim(), limit: 8 },
          signal: controller.signal,
        })
        .then((res) => {
          setHits(res.items);
          setCursor(0);
        })
        .catch(() => {
          // A failed search shows no results rather than an error dialog: the
          // palette is a shortcut, and the full list screen is always reachable.
          setHits([]);
        })
        .finally(() => setLoading(false));
    }, 220);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, open]);

  if (!open) return null;

  const actions = QUICK_ACTIONS.filter((a) => user?.permissions.includes(a.permission as never));
  const total = hits.length + actions.length;

  const go = (index: number) => {
    const hit = hits[index];
    if (hit) {
      navigate(hit.route);
      onClose();
      return;
    }
    const action = actions[index - hits.length];
    if (action) {
      navigate(action.route);
      onClose();
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        zIndex: zIndex.commandPalette,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '110px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('action.search')}
        style={{
          width: '620px',
          maxWidth: '92vw',
          background: color.surface.card,
          borderRadius: radius['2xl'],
          boxShadow: shadow.modal,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '11px',
            padding: '16px 20px',
            borderBottom: `1px solid ${color.border.subtle}`,
          }}
        >
          <span aria-hidden style={{ color: color.brand.primary, fontSize: fontSize.xl }}>
            ⌕
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, Math.max(0, total - 1)));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(0, c - 1));
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                go(cursor);
              }
            }}
            placeholder={t('app.search.placeholder')}
            aria-label={t('app.search.placeholder')}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: fontSize.xl,
              fontFamily: 'inherit',
              color: color.text.primary,
              background: 'transparent',
            }}
          />
          {loading ? (
            <span style={{ fontSize: fontSize.sm, color: color.text.faint }}>
              {t('app.loading')}
            </span>
          ) : null}
        </div>

        <div style={{ padding: '10px 0', maxHeight: '340px', overflowY: 'auto' }}>
          {hits.length > 0 ? (
            <div
              style={{
                padding: '6px 20px',
                fontSize: fontSize.xs,
                fontWeight: fontWeight.black,
                color: color.text.muted,
                letterSpacing: '.06em',
              }}
            >
              النتائج
            </div>
          ) : null}

          {hits.map((hit, i) => (
            <button
              key={hit.id}
              type="button"
              onClick={() => go(i)}
              onMouseEnter={() => setCursor(i)}
              style={{
                width: '100%',
                textAlign: 'start',
                padding: '10px 20px',
                background: i === cursor ? color.brand.soft : 'transparent',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: radius.sm,
                  background: i === cursor ? color.brand.primary : color.surface.muted,
                  color: i === cursor ? '#fff' : color.status.neutral.fg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: fontSize.sm,
                  fontWeight: fontWeight.bold,
                  flexShrink: 0,
                }}
              >
                {hit.title.slice(0, 1)}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{ display: 'block', fontSize: fontSize.md, fontWeight: fontWeight.bold }}
                >
                  {hit.title}
                </span>
                <span style={{ display: 'block', fontSize: fontSize.sm, color: color.text.muted }}>
                  {hit.code ? <Mono size={fontSize.sm}>{hit.code}</Mono> : null}
                  {hit.code && hit.subtitle ? ' · ' : null}
                  {hit.subtitle}
                </span>
              </span>
              {hit.alert ? (
                <span
                  style={{
                    fontSize: fontSize.xs,
                    color: color.status.danger.fg,
                    fontWeight: fontWeight.bold,
                    whiteSpace: 'nowrap',
                  }}
                >
                  ▲ {hit.alert}
                </span>
              ) : null}
            </button>
          ))}

          {actions.length > 0 ? (
            <div
              style={{
                padding: '6px 20px',
                fontSize: fontSize.xs,
                fontWeight: fontWeight.black,
                color: color.text.muted,
                letterSpacing: '.06em',
                marginTop: '6px',
              }}
            >
              إجراءات سريعة
            </div>
          ) : null}

          {actions.map((action, i) => {
            const index = hits.length + i;
            return (
              <button
                key={action.route}
                type="button"
                onClick={() => go(index)}
                onMouseEnter={() => setCursor(index)}
                style={{
                  width: '100%',
                  textAlign: 'start',
                  padding: '9px 20px',
                  fontSize: fontSize.md,
                  cursor: 'pointer',
                  display: 'flex',
                  gap: '10px',
                  border: 'none',
                  background: index === cursor ? color.surface.page : 'transparent',
                  fontFamily: 'inherit',
                }}
              >
                <span aria-hidden style={{ color: color.brand.primary }}>
                  +
                </span>
                {action.labelAr}
              </button>
            );
          })}
        </div>

        <div
          style={{
            padding: '10px 20px',
            background: color.surface.subtle,
            borderTop: `1px solid ${color.border.subtle}`,
            fontSize: fontSize.xs,
            color: color.text.muted,
            display: 'flex',
            gap: '16px',
          }}
        >
          <span>{t('app.search.scopeNote')}</span>
          <span style={{ flex: 1 }} />
          <span dir="ltr" style={{ fontFamily: font.mono }}>
            ↑↓ · ↵ · esc
          </span>
        </div>
      </div>
    </div>
  );
}
